<template>
  <span v-resize="checkMobile">
    <v-card>
      <v-card-title>
        <v-row v-if="!isLoading && investor && investor.name">
          <v-col cols="12" sm="12" class="d-flex justify-start align-center mb-0">
            <h2 class="headline">
              {{ $t("employers.partner") }} - {{ investor.name }} |
              {{ investor.percent }}% |
              {{ translateMode(investor.mode) }}
            </h2>
          </v-col>
          <v-col cols="12" class="mt-0 mb-2">
            <v-divider></v-divider>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12">
            <v-skeleton-loader type="list-item" v-if="isLoading"></v-skeleton-loader>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text v-if="!isLoading && investor && investor.name">
        <v-card v-if="investors_data && investors_data.length > 0" class="mb-4" elevation="2">
          <v-card-text>
            <v-row align="center" justify="center">
              <v-col cols="12" class="text-center">
                <div class="text-h6 grey--text text--darken-1">{{ $t("investor_types.pay_to_partner") }}</div>
                <div class="text-h4 success--text font-weight-bold mt-2">
                  {{ totalInvestorPayment }}{{ company && company.multicurrency_cashboxes ? findCurrencySymbol(base_currency_id) : currency }}
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-data-iterator
          v-if="investors_data && investors_data.length > 0"
          :items="investors_data"
          :items-per-page.sync="itemsPerPage"
          :page.sync="page"
          :total-pages="totalPages"
          sort-by="id"
          hide-default-footer
          mobile-breakpoint="100"
          dense
        >
          <template v-slot:header>
            <v-toolbar
              text
              :color="$vuetify.theme.dark ? '' : 'white'"
              class="mb-2"
              :height="isMobile ? '230px' : ''"
            >
              <v-flex :class="isMobile ? '' : 'd-flex'">
                <v-spacer></v-spacer>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  :label="$t('custom.search')"
                  single-line
                  clearable
                  hide-details
                  class="my-auto"
                  :loading="isLoadingGetCounts"
                ></v-text-field>
                <v-spacer></v-spacer>
                <v-menu
                  v-model="start_date_menu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="start_date_format"
                      :label="$t('custom.start_period')"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      class="mt-5"
                      :loading="isLoadingGetCounts"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="start_date"
                    @input="start_date_menu = false"
                    first-day-of-week="1"
                  ></v-date-picker>
                </v-menu>
                <v-menu
                  v-model="end_date_menu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="end_date_format"
                      :label="$t('custom.end_period')"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :class="isMobile ? 'mt-5' : 'mt-5 ml-3'"
                      :loading="isLoadingGetCounts"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="end_date"
                    @input="end_date_menu = false"
                    first-day-of-week="1"
                  ></v-date-picker>
                </v-menu>
                <v-spacer></v-spacer>
                <v-btn
                  icon
                  @click="toXlsx()"
                  class="mx-2 my-auto"
                  color="success"
                >
                  <v-icon>mdi-file-excel-outline</v-icon>
                </v-btn>
              </v-flex>
            </v-toolbar>
          </template>

          <template v-slot:default="props">
            <v-row>
              <v-col
                v-for="item in props.items"
                :key="item.car_id"
                cols="12"
                sm="6"
                md="4"
                lg="3"
              >
                <v-card>
                  <v-card-title class="subheading font-weight-bold">
                    <h5>{{ item.code }}</h5>
                    <v-spacer></v-spacer>
                    <v-btn
                      icon
                      class="mx-1"
                      x-small
                      color="info"
                      @click="showCarPayments(item)"
                    >
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                  </v-card-title>

                  <v-divider></v-divider>

                  <v-list dense v-if="investor.counts_categories && investor.counts_categories.length > 0">
                    <v-list-item
                      v-for="(key, index) in investor.counts_categories"
                      :key="index"
                    >
                      <v-list-item-content
                        :class="selectColorGroup(key.operation_type)"
                      >
                        {{ translateCategory(key.value) }}:
                      </v-list-item-content>
                      <v-list-item-content>
                        {{ countsByKey(item.counts, key.id) }}
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item>
                      <v-list-item-content>
                        {{ $t("partners.to_pay") }}:
                      </v-list-item-content>
                      <v-list-item-content>
                        {{ calculateForInvestorPayment(item.counts) }}
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                  <v-list dense v-else>
                    <v-list-item
                      v-for="(key, index) in summary_groups"
                      :key="index"
                    >
                      <v-list-item-content
                        :class="selectColorGroupOld(key.value)"
                      >
                        {{ key.text }}:
                      </v-list-item-content>
                      <v-list-item-content>
                        {{ countsByKeyOld(item.counts, key.value) }}
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <template v-slot:footer>
            <v-row class="mt-2" align="center" justify="center">
              <span class="grey--text">{{ $t("other.car_on_page") }}</span>
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    text
                    dark
                    color="primary"
                    class="ml-2"
                    v-bind="attrs"
                    v-on="on"
                    :loading="isLoading || isLoadingGetCounts"
                  >
                    {{ itemsPerPage }}
                    <v-icon>mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-for="(number, index) in itemsPerPageArray"
                    :key="index"
                    @click="updateItemsPerPage(number)"
                  >
                    <v-list-item-title>{{ number }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <v-spacer></v-spacer>

              <span class="mr-4 grey--text">
                {{
                  $t("other.page_number", {
                    page: page,
                    numberOfPages: totalPages > 0 ? totalPages : 1,
                  })
                }}
              </span>
              <v-btn
                fab
                color="info"
                class="mr-1"
                @click="formerPage"
                :loading="isLoadingGetCounts"
                :disabled="page <= 1 || isLoadingGetCounts"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                fab
                color="info"
                class="ml-1"
                @click="nextPage"
                :loading="isLoadingGetCounts"
                :disabled="page >= totalPages || isLoadingGetCounts"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </v-row>
          </template>
        </v-data-iterator>
        <v-flex v-else class="text-center">
          <h2 class="mb-3">{{ $t("other.no_added_cars") }}</h2>
        </v-flex>
        <v-flex md12 class="mt-4">
          <v-card>
            <v-toolbar text :color="$vuetify.theme.dark ? '' : 'white'" >
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                :label="$t('custom.search')"
                single-line
                clearable
                hide-details
              ></v-text-field>
            </v-toolbar>
            <v-data-table
              :headers="headers_counts"
              :items="investors_counts"
              :search="search_count"
              :loading="isLoading"
              :loading-text="$t('custom.loading_table')"
              :items-per-page="20"
              :sort-by="['id']"
              mobile-breakpoint="100"
              :class="!isMobile ? '' : 'is-mobile'"
              dense
              :sort-desc="[true]"
              :footer-props="tableFooterProps([20, 50, 100, -1])"
            >
              <template slot="item" slot-scope="props">
                <tr
                  :class="
                    props.item.operation && props.item.sum < 0
                      ? 'red--text'
                      : ''
                  "
                >
                  <td class="text-center">{{ props.item.id }}</td>
                  <td class="text-center" nowrap>
                    {{ returnDate(props.item.created_at) }}
                  </td>
                  <!-- <td class="justify-center layout px-0">
                      <v-icon class="mr-2" @click="editCount(props.item)">
                        mdi-pencil-outline
                      </v-icon>
                    </td> -->
                  <td class="text-center">
                    {{ translateCategory(props.item.group) }}
                  </td>
                  <td class="text-center">{{ props.item.description }}</td>
                  <td class="text-center">
                    {{ props.item.sum }}{{ currency }}
                  </td>
                  <td class="text-center" v-if="props.item.operation">
                    <v-icon class="text-center" style="color: green">
                      mdi-plus-circle-outline
                    </v-icon>
                  </td>
                  <td class="text-center" v-if="!props.item.operation">
                    <v-icon class="text-center" style="color: tomato">
                      mdi-minus-circle-outline
                    </v-icon>
                  </td>
                  <td class="text-center" v-if="props.item.cash">
                    <v-icon
                      class="text-center"
                      color="success"
                      v-tooltip="{
                        content: $t('tooltips.cash_count'),
                        placement: 'bottom-center',
                        classes: ['info'],
                        targetClasses: ['it-has-a-tooltip'],
                        delay: {
                          show: 1000,
                          hide: 500,
                        },
                      }"
                    >
                      mdi-cash
                    </v-icon>
                  </td>
                  <td class="text-center" v-if="props.item.cashless">
                    <v-icon
                      class="text-center"
                      color="warning"
                      v-tooltip="{
                        content: $t('tooltips.terminal_count'),
                        placement: 'bottom-center',
                        classes: ['info'],
                        targetClasses: ['it-has-a-tooltip'],
                        delay: {
                          show: 1000,
                          hide: 500,
                        },
                      }"
                    >
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </td>
                  <td class="text-center" v-if="props.item.entity">
                    <v-icon
                      class="text-center"
                      color="secondery"
                      v-tooltip="{
                        content: $t('tooltips.entity_count'),
                        placement: 'bottom-center',
                        classes: ['info'],
                        targetClasses: ['it-has-a-tooltip'],
                        delay: {
                          show: 1000,
                          hide: 500,
                        },
                      }"
                    >
                      mdi-bank
                    </v-icon>
                  </td>
                  <td class="text-center" v-if="props.item.cash_card">
                    <v-icon
                      class="text-center"
                      color="info"
                      v-tooltip="{
                        content: $t('money.card_to_card'),
                        placement: 'bottom-center',
                        classes: ['info'],
                        targetClasses: ['it-has-a-tooltip'],
                        delay: {
                          show: 1000,
                          hide: 500,
                        },
                      }"
                    >
                      mdi-credit-card-check-outline
                    </v-icon>
                  </td>
                  <td
                    class="text-left"
                    nowrap
                    v-if="
                      props.item.booking_id &&
                      props.item.car_id &&
                      props.item.car_code
                    "
                  >
                    <p>
                      {{ $t("bookings.booking") }}:
                      <router-link
                        :to="{ path: `/bookings/${props.item.booking_id}` }"
                      >
                        {{ props.item.booking_id }}
                      </router-link>
                    </p>
                    <p>
                      {{ $t("custom.car") }}:
                      <router-link
                        :to="{ path: `/cars/${props.item.car_id}` }"
                      >
                        {{ props.item.car_code }}
                      </router-link>
                    </p>
                  </td>
                  <td
                    class="text-center"
                    v-else-if="props.item.car_id && props.item.car_code"
                  >
                    <router-link
                      :to="{ path: `/cars/${props.item.car_id}` }"
                      >{{ props.item.car_code }}</router-link
                    >
                  </td>
                  <td class="text-center" v-else-if="props.item.source">
                    {{ props.item.source }}
                  </td>
                  <td class="text-center" v-else-if="props.item.investor_id">
                    <router-link
                      :to="{ path: `/investors/${props.item.investor_id}` }"
                      >{{ $t("employers.partner") }} {{ $t("custom.number")
                      }}{{ props.item.investor_id }}</router-link
                    >
                  </td>
                  <td class="text-center" v-else-if="props.item.debt_id">
                    {{ $t("debts.debt") }} {{ $t("custom.number")
                    }}{{ props.item.debt_id }}
                  </td>
                  <td class="text-center" v-else-if="props.item.agent_id">
                    {{ $t("agents.agent") }} {{ $t("custom.number") }}
                    <router-link
                      :to="{ path: `/agent_card/${props.item.agent_id}` }"
                    >
                      {{ props.item.agent_id }}
                    </router-link>
                  </td>
                  <td class="text-center" v-else>-</td>
                </tr>
              </template>
              <template slot="no-data">
                {{ $t("custom.no_data_in_table") }}
              </template>
              <v-alert
                slot="no-results"
                :value="true"
                color="error"
                icon="mdi-alert"
              >
                {{ $t("tables.no_search_result", { msg: search }) }}
              </v-alert>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-card-text>
      <v-card-text v-else>
        <v-row>
          <v-col cols="12">
            <v-skeleton-loader type="table-tbody" v-if="isLoading"></v-skeleton-loader>
            <v-skeleton-loader type="table-tbody" v-if="isLoading && !isMobile"></v-skeleton-loader>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-dialog
      v-model="carPaymentsDialog"
      :retain-focus="false"
      max-width="1000px"
    >
      <v-card>
        <v-card-title>
          <v-row>
            <v-col cols="12" class="d-flex justify-space-between align-center">
              <h2>{{ $t("counts.counts") }}</h2>
              <v-btn icon @click="(carPaymentsDialog = false, car_payments = [])">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text>
          <v-data-table :headers="counts_headers" :items="car_payments"
              :loading-text="$t('custom.loading_table')" :items-per-page="10" mobile-breakpoint="100" sort-by="id"
            sort-desc dense :footer-props="{
              pageText: `{0} ${$t('custom.of')} {1}`,
              itemsPerPageText: '',
              showFirstLastPage: false,
              itemsPerPageOptions: [10, 20, 50, 100, -1],
            }">
            <template slot="item" slot-scope="props">
              <tr>
                <td class="text-center" :key="props.item.id">
                  {{ props.item.id }}
                </td>
                <td class="text-center">
                  {{ formatCreatedAt(props.item.created_at) }}
                </td>
                <td v-if="company && company.multicurrency_cashboxes" class="text-center">
                  {{ props.item.operation ? "" : "-" }}{{ props.item.sum
                  }}{{ findCurrencySymbol(props.item.currency_id) }}
                </td>
                <td v-else class="text-center">
                  {{ props.item.operation ? "" : "-" }}{{ props.item.sum
                  }}{{ currency }}
                </td>
                <td class="text-center" v-if="props.item.cash">
                  <v-icon class="text-center" color="success" v-tooltip="{
                    content: $t('money.cash'),
                    placement: 'bottom-center',
                    classes: ['info'],
                    targetClasses: ['it-has-a-tooltip'],
                    delay: {
                      show: 500,
                      hide: 500,
                    },
                  }">
                    mdi-cash
                  </v-icon>
                </td>
                <td class="text-center" v-if="props.item.cashless">
                  <v-icon class="text-center" color="warning" v-tooltip="{
                    content: $t('money.terminal'),
                    placement: 'bottom-center',
                    classes: ['info'],
                    targetClasses: ['it-has-a-tooltip'],
                    delay: {
                      show: 500,
                      hide: 500,
                    },
                  }">
                    mdi-contactless-payment-circle-outline
                  </v-icon>
                </td>
                <td class="text-center" v-if="props.item.entity">
                  <v-icon class="text-center" color="secondary" v-tooltip="{
                    content: $t('money.to_entity'),
                    placement: 'bottom-center',
                    classes: ['info'],
                    targetClasses: ['it-has-a-tooltip'],
                    delay: {
                      show: 500,
                      hide: 500,
                    },
                  }">
                    mdi-bank
                  </v-icon>
                </td>
                <td class="text-center" v-if="props.item.cash_card">
                  <v-icon class="text-center" color="info" v-tooltip="{
                    content: $t('money.card_to_card'),
                    placement: 'bottom-center',
                    classes: ['info'],
                    targetClasses: ['it-has-a-tooltip'],
                    delay: {
                      show: 500,
                      hide: 500,
                    },
                  }">
                    mdi-credit-card-check-outline
                  </v-icon>
                </td>
                <td class="text-center" v-if="props.item.client_balance">
                  <v-icon class="text-center" color="error" v-tooltip="{
                    content: $t('money.client_balance'),
                    placement: 'bottom-center',
                    classes: ['info'],
                    targetClasses: ['it-has-a-tooltip'],
                    delay: {
                      show: 500,
                      hide: 500,
                    },
                  }">
                    mdi-account-arrow-right
                  </v-icon>
                </td>
                <td class="text-center">
                  {{ translateCategory(props.item.group) }}
                </td>
                <td>
                  {{ props.item.description }}
                </td>
              </tr>
            </template>
            <template slot="no-data">
              {{ $t("custom.no_data_in_table") }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import {
  translateCountCategory,
  companyCountCategories,
  summaryCategoryValues,
  INVESTOR_SUMMARY_CODES,
} from "@/utils/countCategory";
import moment from "moment";
import { setMomentLocale } from "@/utils/localeInfra";
import "moment/locale/ru";
import "moment/locale/en-gb";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { saveAs } from "file-saver";
import xlsx from "json-as-xlsx";
import { investorCountClass, partnerCategoryIds } from "@/helpers/investorPayout";
// moment.locale("ru");
export default {
  pullToRefresh: true,
  data() {
    var self = this;
    return {
      investor: null,
      investors_data: [],
      paidCar: null,
      count: {},
      counts: [],
      carPaymentsDialog: false,
      car_payments: [],
      investors_counts: [],
      itemsPerPageArray: [4, 8, 12],
      filter: {},
      sortDesc: false,
      page: 1,
      itemsPerPage: 4,
      totalPages: 0,
      sortBy: "name",
      isLoadingGetCounts: false,
      counts_headers: [
        { text: this.$t("custom.number"), align: "center", value: "id" },
        { text: this.$t("custom.created_at"), value: "created_at", align: "center" },
        { text: this.$t("custom.amount"), value: "sum", align: "center" },
        { text: this.$t("custom.type"), value: "type", align: "center" },
        { text: this.$t("custom.group"), value: "group", align: "center" },
        { text: this.$t("custom.description"), value: "description", align: "center" }
      ],
      cars: [],
      selected_cars: [],
      search: null,
      search_car: null,
      search_count: null,
      start_date: moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
      end_date: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      start_date_format: moment().subtract(1, "month").startOf("month").format("DD-MM-YYYY"),
      end_date_format: moment().subtract(1, "month").endOf("month").format("DD-MM-YYYY"),
      start_date_menu: false,
      end_date_menu: false,
      editInvestorDialog: false,
      headers_counts: [
        { text: this.$t("custom.number"), align: "center", value: "id" },
        {
          text: this.$t("custom.created_at"),
          value: "created_at",
          align: "center",
        },
        { text: this.$t("custom.group"), value: "group", align: "center" },
        {
          text: this.$t("custom.description"),
          value: "description",
          align: "center",
        },
        { text: this.$t("custom.amount"), value: "sum", align: "center" },
        {
          text: this.$t("custom.operation"),
          value: "operation",
          align: "center",
        },
        { text: this.$t("custom.type"), value: "cashless", align: "center" },
        { text: this.$t("custom.source"), sortable: false, align: "center" },
      ],
      selectCarDialog: false,
      paymentDialog: false,
      isMobile: false,
      isLoadingCars: false,
      isLoading: false,
      error: "",
      searchTimeout: null,
    };
  },
  mounted() {
    let self = this;
  },
  created() {
    // moment Locale
    setMomentLocale(this.$i18n.locale);
    if (this.$store.getters.isLoggedIn) {
      if (
        this.$store.getters.getCurrentUser.role == "partner"
      ) {
        this.getCounts(false);
      } else {
        this.$router.push("/dashboard");
        this.$notify(this.$t("errors.not_enough_data"), "", "warning");
      }
    } else {
      this.$store.commit("unsetCurrentUser");
    }
  },
  watch: {
    search(val) {
      // Debounce для серверного поиска
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.page = 1; // Сбрасываем на первую страницу при поиске
        this.getCounts(true);
      }, 500);
    },
    start_date() {
      if (this.start_date) {
        this.start_date_format = moment(this.start_date, "YYYY-MM-DD").format(
          "DD-MM-YYYY"
        );

        if (this.start_date && this.end_date) {
          this.getCounts(true);
        }
      }
    },
    end_date() {
      if (this.end_date) {
        this.end_date_format = moment(this.end_date, "YYYY-MM-DD").format(
          "DD-MM-YYYY"
        );

        if (this.start_date && this.end_date) {
          this.getCounts(true);
        }
      }
    },
    page(val) {
      if (this.start_date && this.end_date) {
        this.getCounts(true);
      }
    },
    itemsPerPage(val) {
      if (this.start_date && this.end_date) {
        this.getCounts(true);
      }
    },
  },
  computed: {
    // Сводка по статьям у инвестора без выбранных статей: статьи каталога в прежнем
    // порядке плюс статьи компании без кода, по которым на странице есть платежи
    // (FT-192 task-24). Одна на все карточки — строки у автомобилей совпадают.
    summary_groups() {
      const categories = companyCountCategories(this.$store.getters.getCompany, { fallback: true });
      return summaryCategoryValues(INVESTOR_SUMMARY_CODES, categories, this.counts).map((value) => ({
        text: this.translateCategory(value),
        value,
      }));
    },
    numberOfPages() {
      return Math.ceil(this.investors_data.length / this.itemsPerPage);
    },
    currency() {
      if (
        this.$store.getters.getCompany &&
        this.$store.getters.getCompany.company &&
        this.$store.getters.getCompany.company.currency
      ) {
        return this.$store.getters.getCompany.company.currency;
      } else {
        return "";
      }
    },
    currencies() {
      if (
        this.$store.getters.getCompany &&
        this.$store.getters.getCompany.company &&
        this.$store.getters.getCompany.company.currencies
      ) {
        return this.$store.getters.getCompany.company.currencies;
      } else {
        return [];
      }
    },
    isNative() {
      return Capacitor.isNativePlatform();
    },
    checkWriteFile() {
      return Capacitor.isNativePlatform() && Filesystem.checkPermissions();
    },
    requestPermissionsWriteFile() {
      return Filesystem.requestPermissions();
    },
    company() {
      return this.$store.getters.getCompany.company;
    },
    totalInvestorPayment() {
      if (this.investors_data && this.investors_data.length > 0 && this.investor && this.investor.counts_categories) {
        let total = 0;
        this.investors_data.forEach((item) => {
          total += parseFloat(this.calculateForInvestorPayment(item.counts));
        });
        return total.toFixed(2);
      } else {
        return '0.00';
      }
    },
  },
  methods: {
    getCounts(manual = false) {
      if (this.start_date && this.end_date) {
        if (manual) {
          this.isLoadingGetCounts = true;
        } else {
          this.isLoading = true;
        }
        let url = `/api/v1/investor_analytics?start_date=${this.start_date}&end_date=${this.end_date}&page=${this.page}&per_page=${this.itemsPerPage}`;
        if (this.search && this.search.trim() !== '') {
          url += `&search=${encodeURIComponent(this.search.trim())}`;
        }
        this.axios
          .get(
            url,
            {
              headers: {
                Authorization: this.$store.getters.getAuthToken,
              },
            }
          )
          .then((response) => {
            this.investor = response.data.investor;
            this.investors_data = response.data.data;
            this.counts_categories = response.data.counts_categories_all;
            this.counts = [];
            response.data.data.forEach((item) => {
              this.counts = this.counts.concat(item.counts);
            });
            this.investors_counts = response.data.investors_counts;
            this.totalPages = response.data.total_pages;
          })
          .catch((error) => {
            this.setError(error, this.$t("errors.info_load"));
          })
          .finally(() => (this.isLoading = false, this.isLoadingGetCounts = false));
      } else {
        this.$notify(this.$t("errors.not_enough_data"), "", "warning");
      }
    },
    category_ids_in_all_branches(id) {
      let this_branch_category = this.counts_categories.find((category) => category.id == id);
      let counts_categories_ids = [this_branch_category.id];
      if (this.counts_categories.filter((category) => category.value == this_branch_category.value).length > 1) {
        this.counts_categories.filter((category) => (category.value == this_branch_category.value && category.id != this_branch_category.id)).forEach((category) => {
          counts_categories_ids.push(category.id);
        });
      }
      return counts_categories_ids;
    },
    countsByKey(counts, counts_category_id) {
      let plus_total = 0;
      let minus_total = 0;
      const sumPropertyValue = (items, prop) => items.reduce((a, b) => parseFloat(a) + parseFloat(b[prop]), 0);
      if (this.company && this.company.multicurrency_cashboxes) {
        let filtered_counts_plus_in_base_currency = counts.filter(
          (count) => this.category_ids_in_all_branches(counts_category_id).includes(count.counts_category_id) && count.operation == true && count.currency_id == this.base_currency_id
        );
        let filtered_counts_minus_in_base_currency = counts.filter(
          (count) => this.category_ids_in_all_branches(counts_category_id).includes(count.counts_category_id) && count.operation == false && count.currency_id == this.base_currency_id
        );
        let filtered_counts_plus_with_other_currency = counts.filter(
          (count) => this.category_ids_in_all_branches(counts_category_id).includes(count.counts_category_id) && count.operation == true && count.currency_id != this.base_currency_id
        );
        let filtered_counts_minus_with_other_currency = counts.filter(
          (count) => this.category_ids_in_all_branches(counts_category_id).includes(count.counts_category_id) && count.operation == false && count.currency_id != this.base_currency_id
        );

        const plus_total_in_base_currency = sumPropertyValue(filtered_counts_plus_in_base_currency, "sum");
        const minus_total_in_base_currency = sumPropertyValue(filtered_counts_minus_in_base_currency, "sum");
        const plus_total_with_other_currency = sumPropertyValue(filtered_counts_plus_with_other_currency, "rated_sum");
        const minus_total_with_other_currency = sumPropertyValue(filtered_counts_minus_with_other_currency, "rated_sum");
        plus_total = plus_total_in_base_currency + plus_total_with_other_currency;
        minus_total = minus_total_in_base_currency + minus_total_with_other_currency;
      } else {
        let filtered_counts_plus = counts.filter(
          (count) => this.category_ids_in_all_branches(counts_category_id).includes(count.counts_category_id) && count.operation == true
        );
        let filtered_counts_minus = counts.filter(
          (count) => this.category_ids_in_all_branches(counts_category_id).includes(count.counts_category_id) && count.operation == false
        );
        plus_total = sumPropertyValue(filtered_counts_plus, "sum");
        minus_total = sumPropertyValue(filtered_counts_minus, "sum");
      }


      if (plus_total && minus_total) {
        return `+ ${plus_total} | - ${minus_total}`;
      } else if (plus_total && !minus_total) {
        return `+ ${plus_total}`;
      } else if (!plus_total && minus_total) {
        return `- ${minus_total}`;
      } else {
        return "-";
      }
    },
    countsByKeyOld(counts, key) {
      let plus_total = 0;
      let minus_total = 0;
      const sumPropertyValue = (items, prop) => items.reduce((a, b) => parseFloat(a) + parseFloat(b[prop]), 0);
      if (this.company && this.company.multicurrency_cashboxes) {
        let filtered_counts_plus_in_base_currency = counts.filter(
          (count) => count.group == key && count.operation == true && count.currency_id == this.base_currency_id
        );
        let filtered_counts_minus_in_base_currency = counts.filter(
          (count) => count.group == key && count.operation == false && count.currency_id == this.base_currency_id
        );
        let filtered_counts_plus_with_other_currency = counts.filter(
          (count) => count.group == key && count.operation == true && count.currency_id != this.base_currency_id
        );
        let filtered_counts_minus_with_other_currency = counts.filter(
          (count) => count.group == key && count.operation == false && count.currency_id != this.base_currency_id
        );

        const plus_total_in_base_currency = sumPropertyValue(filtered_counts_plus_in_base_currency, "sum");
        const minus_total_in_base_currency = sumPropertyValue(filtered_counts_minus_in_base_currency, "sum");
        const plus_total_with_other_currency = sumPropertyValue(filtered_counts_plus_with_other_currency, "rated_sum");
        const minus_total_with_other_currency = sumPropertyValue(filtered_counts_minus_with_other_currency, "rated_sum");
        plus_total = plus_total_in_base_currency + plus_total_with_other_currency;
        minus_total = minus_total_in_base_currency + minus_total_with_other_currency;
      } else {
        let filtered_counts_plus = counts.filter(
          (count) => count.group == key && count.operation == true
        );
        let filtered_counts_minus = counts.filter(
          (count) => count.group == key && count.operation == false
        );

        plus_total = sumPropertyValue(filtered_counts_plus, "sum");
        minus_total = sumPropertyValue(filtered_counts_minus, "sum");
      }

      if (plus_total && minus_total) {
        return `+ ${plus_total} | - ${minus_total}`;
      } else if (plus_total && !minus_total) {
        return `+ ${plus_total}`;
      } else if (!plus_total && minus_total) {
        return `- ${minus_total}`;
      } else {
        return "-";
      }
    },
    calculateForInvestorPayment(counts) {
      let expenses = 0;
      let income = 0;
      let payment_for_investor = 0;
      let investor_counts_categories_ids = partnerCategoryIds(this.investor.counts_categories, this.counts_categories);
      // Фильруем счета, которые относятся к инвестору
      counts = counts.filter((count) => investor_counts_categories_ids.includes(count.counts_category_id) && count.completed);
      counts.forEach((count) => {
        // Определяем категорию счета
        const counts_category = this.counts_categories.find((category) => category.id == count.counts_category_id);
        const klass = investorCountClass(counts_category, count);
        // Залоги и внутренние переводы — оборот, а не доход или расход партнёра
        if (!klass) return;
          // В зависимости от investor.mode определяем, что считать и вычисляем сумму согласно investor.percent
          if (this.investor.mode == "Доходы")  {
            if (klass.income) {
              // Доход
              if (this.company && this.company.multicurrency_cashboxes && count.rated_sum && (count.rated_sum > 0 || count.rated_sum < 0)) {
                payment_for_investor += klass.sign * (count.rated_sum - (count.rated_sum * (1 - (parseInt(this.investor.percent)) / 100)));
              } else {
                payment_for_investor += klass.sign * (count.sum - (count.sum * (1 - (parseInt(this.investor.percent)) / 100)));
              }
            }
          } else if (this.investor.mode == "Доходы - Расходы") {
            if (klass.income) {
              // Доход
              if (this.company && this.company.multicurrency_cashboxes && count.rated_sum && (count.rated_sum > 0 || count.rated_sum < 0)) {
                payment_for_investor += klass.sign * (count.rated_sum - (count.rated_sum * (1 - (parseInt(this.investor.percent)) / 100)));
              } else {
                payment_for_investor += klass.sign * (count.sum - (count.sum * (1 - (parseInt(this.investor.percent)) / 100)));
              }
            } else {
              // Расход
              if (this.company && this.company.multicurrency_cashboxes && count.rated_sum && (count.rated_sum > 0 || count.rated_sum < 0)) {
                payment_for_investor -= klass.sign * (count.rated_sum - (count.rated_sum * (1 - (parseInt(this.investor.percent)) / 100)));
              } else {
                payment_for_investor -= klass.sign * (count.sum - (count.sum * (1 - (parseInt(this.investor.percent)) / 100)));
              }
            }
          } else if (this.investor.mode == "Доходы - (Комиссия партнера - Расходы)") {
            if (klass.income) {
              // Доход
              if (this.company && this.company.multicurrency_cashboxes && count.rated_sum && (count.rated_sum > 0 || count.rated_sum < 0)) {
                income += klass.sign * count.rated_sum;
              } else {
                income += klass.sign * count.sum;
              }
            } else {
              // Расход
              if (this.company && this.company.multicurrency_cashboxes && count.rated_sum && (count.rated_sum > 0 || count.rated_sum < 0)) {
                expenses += klass.sign * count.rated_sum;
              } else {
                expenses += klass.sign * count.sum;
              }
            }
            payment_for_investor = (income * (parseInt(this.investor.percent) / 100)) - expenses;
          }
      });

      return parseFloat(payment_for_investor).toFixed(2);
    },
    findCurrencySymbol(id) {
      if (id != null) {
        if (this.currencies.find((currency) => currency.id == id)) {
          return this.currencies.find((currency) => currency.id == id).symbol;
        }
      }
    },
    toXlsx() {
      if (!this.isGuest) {
        let filtered_counts = [];
        let investor_counts_categories_ids = partnerCategoryIds(this.investor.counts_categories, this.counts_categories);
        // Фильруем счета, которые относятся к инвестору
        filtered_counts = this.counts.filter((count) => investor_counts_categories_ids.includes(count.counts_category_id) && count.completed);
        let data = [
          {
            sheets: "Payments",
            columns: [
              { label: "ID", value: "id" },
              { label: this.$t("custom.created_at"), value: (row) => {
                  return moment(row.created_at).format("DD.MM.YYYY H:mm");
                }
              },
              { label: this.$t("custom.car"), value: "car_code" },
              { label: this.$t("custom.amount"), value: "sum" },
              { label: this.$t("custom.operation"), value: (row) => {
                  if (row.operation) {
                    return "+";
                  } else {
                    return "-";
                  }
                }
              },
              { label: this.$t("counts.about"), value: "description" },
              { label: this.$t("investor_types.payment_type"), value: (row) => {
                if (row.cashless) {
                  return this.$t("money.terminal");
                } else if (row.cash_card) {
                  return this.$t("money.card_to_card");
                } else if (row.cash) {
                  return this.$t("money.cash");
                } else if (row.entity) {
                  return this.$t("money.from_entity");
                } else {
                  return "-";
                }
              }},
              { label: this.$t("custom.group"), value: (row) => {
                return this.translateCategory(row.group);
              }},
              { label: this.$t("bookings.booking"), value: "booking_id" },
              { label: this.$t("counts.completed"), value: (row) => {
                if (row.completed) {
                  return this.$t("custom.yes");
                } else {
                  return this.$t("custom.no");
                }
              }},
            ],
            content: filtered_counts,
          },
        ];
        let settings = {
          fileName: "cars_payments", // Name of the resulting spreadsheet
          extraLength: 3, // A bigger number means that columns will be wider
          writeMode: "write", // The available parameters are 'WriteFile' and 'write'. This setting is optional. Useful in such cases https://docs.sheetjs.com/docs/solutions/output#example-remote-file
          writeOptions: {
            bookType: "xlsx",
            type: "array",
          }, // Style options from https://docs.sheetjs.com/docs/api/write-options
          RTL: false, // Display the columns from right-to-left (the default value is false)
        };
        let wbout = xlsx(data, settings);
        var blob_data = new Blob([new Uint8Array(wbout)], {type:"application/octet-stream"});
        if (!this.isNative) {
          saveAs(blob_data, 'cars_payments.xlsx');
        } else {
          if (Filesystem.checkPermissions()) {
            const getBase64FromBlob = async (blob) => {
              return new Promise((resolve) => {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onloadend = () => {
                  const base64data = reader.result;
                  resolve(base64data);
                }
              });
            }
            getBase64FromBlob(blob_data).then(
              (base64) => {
                // save file
                async function writeFile() {
                  return await Filesystem.writeFile({
                    path: 'cars_payments.xlsx',
                    data: base64,
                    directory: Directory.Documents,
                  });
                }
                let file_uri = null
                let writedFile = writeFile().then((getUriResult) => {
                  // share file opened file
                  let shareFile = async () => {
                    await Share.share({
                      title: 'Download document',
                      files: [getUriResult.uri],
                    }).catch((error) => {
                      console.log("shareFile", error)
                    });
                  };
                  let sharedFile = shareFile();
                }).catch((error) => {
                  console.log("shareFile", error)
                });
              }
            );
          } else {
            Filesystem.requestPermissions();
          }
        }
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    selectColorGroup(operation_type) {
      if (operation_type) {
        return "success--text";
      } else {
        return "red--text";
      }
    },
    selectColorGroupOld(key) {
      if (key == "Оплата аренды") {
        return "success--text";
      } else if (key == "Выплаты партнёрам") {
        return "red--text";
      }
    },
    checkAdmin() {
      return this.$store.getters.isSuperAdmin || this.$store.getters.isAdmin;
    },
    loadCars() {
      this.isLoading = true;
      this.axios
        .get("/api/v1/index_for_new_count", {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          response.data.data.forEach((element) => {
            this.cars.push(element.attributes);
          });
        })
        .catch((error) => {
          this.setError(error, this.$t("errors.cars_load"));
        })
        .finally(() => (this.isLoading = false));
    },
    translateMode(mode) {
      let group_text = "";
      switch (mode) {
        case "Доходы":
          group_text = this.$t("investor_types.income");
          break;
        case "Доходы - Расходы":
          group_text = this.$t("investor_types.income_expences");
          break;
        case "Доходы - (Комиссия партнера - Расходы)":
          group_text = this.$t("investor_types.income_expences_with_commission");
          break;
      }
      return group_text;
    },
    translateCategory(category) {
      return translateCountCategory((key) => this.$t(key), category);
    },
    returnDate(item) {
      return moment(item, "YYYY-MM-DD").format("LL");
    },
    nextPage() {
      if (this.page + 1 <= this.totalPages) this.page += 1;
    },
    formerPage() {
      if (this.page - 1 >= 1) this.page -= 1;
    },
    updateItemsPerPage(number) {
      this.itemsPerPage = number;
    },
    showCarPayments(counts) {
      this.carPaymentsDialog = true;
      // Фильтруем платежи согласно категориям партнера
      let investor_counts_categories_ids = partnerCategoryIds(this.investor.counts_categories, this.counts_categories);
      this.car_payments = counts.counts.filter((count) => investor_counts_categories_ids.includes(count.counts_category_id));
    },
    formatCreatedAt(date) {
      return moment.parseZone(date).format("DD MMM H:mm");
    },
    goBack() {
      var numberOfEntries = window.history.length;
      if (numberOfEntries > 2) {
        this.$router.go(-1);
      } else {
        if (
          this.PageData &&
          this.PageData.backCrumb &&
          this.PageData.backCrumb.url
        ) {
          var fpath = this.PageData.backCrumb.url;
          this.$router.push({
            path: fpath,
          });
        } else {
          this.$router.push({ path: "/dashboard" });
        }
      }
    },
    checkMobile() {
      if (this.$vuetify.breakpoint.name == "xs" || this.$vuetify.breakpoint.name == "sm") {
        this.isMobile = true;
        return true;
      } else {
        this.isMobile = false;
        return false;
      }
    },
  },
  components: {},
};
</script>
